import { useState, useEffect } from "react";
import { ItemPriceManager } from "./ItemPriceManager";
import { getProductPricingModel, createProductPricingModel, getProduct, updateProduct, listItemPrices, listPromotions, listProductBundleItems, type PricingModel, type Product, type Promotion } from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Select } from "../../primitives/Select";
import { Modal } from "../../primitives/Modal";
import { ProductAlerts } from "./ProductAlerts";
import { useProductAlerts } from "./useProductAlerts";
import { Tag, Calendar, Percent } from "lucide-react";

type ProductPricingTabProps = {
  productId: string;
};

const PRICING_MODE_OPTIONS = [
  { value: "fixed", label: "Prix fixe" },
  { value: "per_unit", label: "Par quantité" },
];
/** Option réservée aux bundles : prix = somme des composants sélectionnés (options activées). */
const BUNDLE_PRICING_MODE_OPTIONS = [
  ...PRICING_MODE_OPTIONS,
  { value: "variable_components", label: "Variable (composants)" },
];

/** Choix d'unité pour le mode "Par quantité". */
const QUANTITY_UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: "each", label: "Chaque" },
  { value: "hour", label: "Heure" },
  { value: "hectare", label: "Hectare" },
  { value: "acre", label: "Acre" },
  { value: "project", label: "Projet" },
  { value: "month", label: "Mois" },
  { value: "year", label: "Année" },
  { value: "credit", label: "Crédit" },
];

/** Choix pour le mode "Par superficie" (acres ou hectare). */
const AREA_UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: "hectare", label: "Hectare" },
  { value: "acre", label: "Acre" },
];

const VISIBILITY_OPTIONS: { value: Product["visibility_scope"]; label: string }[] = [
  { value: "internal", label: "Interne uniquement" },
  { value: "ambassador", label: "Ambassadeurs" },
  { value: "end_customer", label: "Clients finaux" },
  { value: "both", label: "Ambassadeurs et clients" },
];

export function ProductPricingTab({ productId }: ProductPricingTabProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const [pricingModel, setPricingModel] = useState<PricingModel | null>(null);
  const [itemPrices, setItemPrices] = useState<Array<{ price_type: string; unit_price: number }>>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [hasOptionalBundleItem, setHasOptionalBundleItem] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingModel, setIsEditingModel] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    mode: "fixed" as PricingModel["mode"],
    unit: "each",
    area_unit: "hectare",
    params: null as Record<string, any> | null,
  });

  // Load product, item prices, and promotions for alerts
  useEffect(() => {
    Promise.all([
      getProduct(productId).then(setProduct).catch(() => null),
      listItemPrices(productId).then((prices) => setItemPrices(prices.map((p) => ({ price_type: p.price_type, unit_price: p.unit_price })))).catch(() => []),
      listPromotions({ status_filter: "active" }).then((promos) => {
        // Filtrer les promotions qui s'appliquent à ce produit
        const now = new Date();
        const applicablePromos = promos.filter((p) => {
          // Vérifier la période
          const startDate = new Date(p.start_date);
          const endDate = new Date(p.end_date);
          if (now < startDate || now > endDate) return false;
          
          // Vérifier la portée
          if (p.scope === "all_products") return true;
          if (p.scope === "selected_products" && p.product_ids?.includes(productId)) return true;
          if (p.scope === "product_categories") {
            // TODO: Implémenter la vérification par catégories
            return false;
          }
          return false;
        });
        setPromotions(applicablePromos);
      }).catch(() => {
        setPromotions([]);
      }),
    ]);
  }, [productId]);

  // Pour les bundles : détecter si au moins un composant est optionnel (pour pré-sélectionner variable_components)
  useEffect(() => {
    if (!product || product.product_type !== "bundle") {
      setHasOptionalBundleItem(false);
      return;
    }
    listProductBundleItems(productId)
      .then((items) => {
        const hasOptional = items.some((i) => !i.is_required);
        setHasOptionalBundleItem(hasOptional);
      })
      .catch(() => setHasOptionalBundleItem(false));
  }, [productId, product?.id, product?.product_type]);

  const alerts = useProductAlerts(product, null, pricingModel, itemPrices, [], []);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getProductPricingModel(productId)
      .then((model) => {
        if (!mounted) return;
        if (model) {
          setPricingModel(model);
          const displayMode = model.mode === "computed" ? "per_unit" : model.mode;
          const params = model.params ?? {};
          setForm({
            mode: displayMode as PricingModel["mode"],
            unit: (params.unit as string) || "each",
            area_unit: (params.area_unit as string) || "hectare",
            params: model.params,
          });
        } else {
          setPricingModel(null);
          setForm({
            mode: "fixed",
            unit: "each",
            area_unit: "hectare",
            params: null,
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load pricing model:", error);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [productId]);

  // Auto-sélection du mode "variable_components" quand on ouvre le formulaire de création pour un bundle avec au moins un item optionnel
  useEffect(() => {
    if (isEditingModel && !pricingModel && hasOptionalBundleItem && product?.product_type === "bundle") {
      setForm((f) => (f.mode !== "variable_components" ? { ...f, mode: "variable_components" as PricingModel["mode"] } : f));
    }
  }, [isEditingModel, pricingModel, hasOptionalBundleItem, product?.product_type]);

  const handleSaveModel = async () => {
    setIsSaving(true);
    try {
      const params =
        form.mode === "per_unit" ? { unit: form.unit } : form.mode === "variable_components" ? null : null;
      const updated = await createProductPricingModel(productId, {
        mode: form.mode,
        base_rate: 0,
        min_price: null,
        params,
      });
      setPricingModel(updated);
      setIsEditingModel(false);
    } catch (error) {
      console.error("Failed to save pricing model:", error);
      alert(`Erreur lors de la sauvegarde: ${(error as Error).message || "Veuillez réessayer."}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (pricingModel) {
      const displayMode = (pricingModel.mode === "computed" ? "per_unit" : pricingModel.mode) as PricingModel["mode"];
      const params = pricingModel.params ?? {};
      setForm({
        mode: displayMode,
        unit: (params.unit as string) || "each",
        area_unit: "hectare",
        params: pricingModel.params,
      });
    } else {
      setForm({
        mode: "fixed",
        unit: "each",
        area_unit: "hectare",
        params: null,
      });
    }
    setIsEditingModel(false);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  const productUsage = (product as { product_usage?: string } | null)?.product_usage ?? "sold";
  if (productUsage === "internal_consumption") {
    return (
      <div className="p-6 space-y-6">
        <div className="rounded-md border border-border bg-surface-muted p-4">
          <p className="text-sm font-medium text-text">Ce produit est utilisé en interne</p>
          <p className="text-sm text-muted mt-1">
            Il n&apos;est pas vendu. Configurez le fournisseur dans Approvisionnement et les coûts internes si besoin.
          </p>
        </div>
      </div>
    );
  }

  const handleVisibilityChange = async (value: string) => {
    const option = VISIBILITY_OPTIONS.find((o) => o.label === value);
    if (!option || !product) return;
    try {
      const updated = await updateProduct(productId, { visibility_scope: option.value });
      setProduct(updated);
    } catch (err) {
      console.error("Failed to update visibility:", err);
      alert(`Erreur: ${(err as Error).message ?? "Veuillez réessayer."}`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Alerts */}
      <ProductAlerts alerts={alerts} />

      {/* Visibilité du prix */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-text">Visibilité du prix</h3>
        <p className="text-xs text-muted">
          Détermine dans quels catalogues (pricebooks) le prix est visible : interne seulement, pricebook ambassadeur, pricebook public, ou les deux.
        </p>
        {product && (
          <Select
            options={VISIBILITY_OPTIONS.map((o) => o.label)}
            value={VISIBILITY_OPTIONS.find((o) => o.value === product.visibility_scope)?.label ?? "Interne uniquement"}
            onChange={handleVisibilityChange}
          />
        )}
      </div>

      {/* Comment calcule-t-on le prix ? (unité = params.unit). Les montants réels sont dans les pricebooks ci‑dessous. */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text">Comment calcule-t-on le prix ?</h3>
          {!isEditingModel && (
            <Button
              label={pricingModel ? "Modifier" : "Créer un modèle"}
              intent="primary"
              size="sm"
              onClick={() => setIsEditingModel(true)}
            />
          )}
        </div>

        {!pricingModel && !isEditingModel ? (
          <div className="rounded-md border border-border bg-surface-muted p-4">
            <p className="text-sm text-muted">
              Définissez comment le prix est calculé (prix fixe ou par quantité). Les montants par catalogue (interne, ambassadeur, public) se configurent dans « Prix par PriceBook » ci‑dessous.
            </p>
          </div>
        ) : isEditingModel ? (
          <div className="space-y-3 rounded-md border border-border bg-surface p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Mode de tarification</label>
                <Select
                  options={(product?.product_type === "bundle" ? BUNDLE_PRICING_MODE_OPTIONS : PRICING_MODE_OPTIONS).map((opt) => opt.label)}
                  value={(product?.product_type === "bundle" ? BUNDLE_PRICING_MODE_OPTIONS : PRICING_MODE_OPTIONS).find((opt) => opt.value === form.mode)?.label || ""}
                  onChange={(value) => {
                    const opts = product?.product_type === "bundle" ? BUNDLE_PRICING_MODE_OPTIONS : PRICING_MODE_OPTIONS;
                    const option = opts.find((opt) => opt.label === value);
                    if (option) setForm({ ...form, mode: option.value as PricingModel["mode"] });
                  }}
                />
              </div>
              {form.mode === "per_unit" && (
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Unité</label>
                  <Select
                    options={QUANTITY_UNIT_OPTIONS.map((o) => o.label)}
                    value={QUANTITY_UNIT_OPTIONS.find((o) => o.value === form.unit)?.label ?? "Chaque"}
                    onChange={(value) => {
                      const opt = QUANTITY_UNIT_OPTIONS.find((o) => o.label === value);
                      if (opt) setForm({ ...form, unit: opt.value });
                    }}
                  />
                  <p className="text-xs text-muted mt-0.5">Unité pour prix × quantité.</p>
                </div>
              )}
            </div>
            {form.mode !== "variable_components" && (
              <p className="text-xs text-muted">Les montants se configurent dans « Prix par PriceBook » ci‑dessous.</p>
            )}
            {form.mode === "variable_components" && (
              <p className="text-xs text-muted">Le prix du bundle sera calculé à partir des composants sélectionnés (soumission). Aucun prix à saisir ici.</p>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                label="Annuler"
                intent="default"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              />
              <Button
                label={isSaving ? "Enregistrement..." : "Enregistrer"}
                intent="primary"
                size="sm"
                onClick={handleSaveModel}
                disabled={isSaving}
              />
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-border bg-surface p-4">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
              <span className="font-medium text-text">
                {BUNDLE_PRICING_MODE_OPTIONS.find((o) => o.value === pricingModel.mode)?.label ?? PRICING_MODE_OPTIONS.find((o) => o.value === pricingModel.mode)?.label ?? pricingModel.mode}
              </span>
              {pricingModel.mode === "per_unit" && pricingModel.params?.unit && (
                <span className="text-muted">
                  Unité: {QUANTITY_UNIT_OPTIONS.find((o) => o.value === pricingModel.params?.unit)?.label ?? pricingModel.params.unit}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Item Prices Section — masqué en mode variable_components (prix = somme des composants) */}
      <div className="space-y-4">
        {(pricingModel?.mode ?? form.mode) === "variable_components" ? (
          <div className="rounded-md border border-border bg-surface-muted p-4">
            <p className="text-sm font-medium text-text">Prix par PriceBook</p>
            <p className="text-sm text-muted mt-1">
              Le prix est calculé à partir des composants sélectionnés dans le bundle. Il apparaît dans la soumission lorsque les options sont choisies.
            </p>
          </div>
        ) : (
          <ItemPriceManager
            productId={productId}
            visibilityScope={product?.visibility_scope ?? "internal"}
            promotions={promotions}
            onUpdate={() => {
              listItemPrices(productId)
                .then((prices) =>
                  setItemPrices(prices.map((p) => ({ price_type: p.price_type, unit_price: p.unit_price })))
                )
                .catch(() => []);
            }}
          />
        )}
      </div>

      {/* Promotions Section */}
      {promotions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text">Promotions actives</h3>
            <Button
              label="Gérer les promotions"
              intent="default"
              size="sm"
              onClick={() => {
                window.location.hash = "#/admin-promotions";
              }}
            />
          </div>
          <div className="space-y-2">
            {promotions.map((promo) => {
              const startDate = new Date(promo.start_date);
              const endDate = new Date(promo.end_date);
              const now = new Date();
              const isUpcoming = startDate > now;
              
              return (
                <div key={promo.id} className="rounded-md border border-border bg-surface p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag size={16} className="text-primary" />
                        <h4 className="text-sm font-medium text-text">{promo.name}</h4>
                        {promo.code && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary">
                            {promo.code}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-xs text-muted">
                        <div className="flex items-center gap-4">
                          <span>
                            {promo.promotion_type === "percentage"
                              ? `${promo.value}% de réduction`
                              : promo.promotion_type === "fixed_amount"
                              ? `${promo.value} CAD de réduction`
                              : `Prix fixe: ${promo.value} CAD`}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {startDate.toLocaleDateString("fr-FR")} - {endDate.toLocaleDateString("fr-FR")}
                          </span>
                        </div>
                        {promo.description && (
                          <p className="text-xs text-muted mt-1">{promo.description}</p>
                        )}
                        {isUpcoming && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning">
                            À venir
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
